import { Setting } from "@prisma/client";
import { formatISO } from "date-fns";
import type React from "react";
import { Fragment, useCallback, useState } from "react";
import { match } from "ts-pattern";
import type { Except } from "type-fest";

import type { FragmentType } from "~/utils/fragment";

import { Checkbox } from "../Checkbox";
import { DialogTitle } from "../DialogTitle";
import { RadioGroup } from "../RadioGroup";
import { Textarea } from "../Textarea";
import { TextField } from "../TextField";
import type { AssignmentForm_AssignmentFragment } from "./AssignmentForm_AssignmentFragment";
import type { AssignmentForm_PackFragment } from "./AssignmentForm_PackFragment";
import { TimeLimitSelect } from "./TimeLimitSelect";

type StrictnessSetting = Extract<Setting, `STRICT_${string}`>;

const isStrictnessSetting = (
  setting: Setting,
): setting is StrictnessSetting => {
  return setting.startsWith("STRICT_");
};

const allStrictnessSettings =
  Object.values(Setting).filter(isStrictnessSetting);

export const AssignmentForm: React.FC<
  React.PropsWithChildren<{
    pack: FragmentType<"Pack", { select: typeof AssignmentForm_PackFragment }>;
    assignment?: FragmentType<
      "Assignment",
      { select: typeof AssignmentForm_AssignmentFragment }
    >;
  }>
> = ({ pack, assignment, children }) => {
  const [today] = useState(() => new Date());
  const [gameMode, setGameMode] = useState<"classic" | "mcq">(
    assignment?.settings.includes("MULTIPLE_CHOICE") ? "mcq" : "classic",
  );
  const [strictnessSettings, setStrictnessSettings] = useState<
    StrictnessSetting[]
  >(assignment?.settings.filter(isStrictnessSetting) ?? []);

  const allStrictnessSettingsEnabled = allStrictnessSettings.every((setting) =>
    strictnessSettings.includes(setting),
  );

  const getCheckboxProps = useCallback<
    (
      setting: Setting,
    ) => Except<React.ComponentProps<typeof Checkbox>, "children">
  >(
    (setting) => {
      return {
        name: "settings",
        value: setting,
        defaultChecked: assignment?.settings.includes(setting),
      };
    },
    [assignment],
  );

  const getStrictnessCheckboxProps = useCallback<
    (
      strictnessSetting: StrictnessSetting,
    ) => Except<React.ComponentProps<typeof Checkbox>, "children">
  >(
    (strictnessSetting) => {
      return {
        name: "settings",
        value: strictnessSetting,
        checked: strictnessSettings.includes(strictnessSetting),
        onChange: (event) => {
          setStrictnessSettings(
            event.target.checked
              ? [...strictnessSettings, strictnessSetting]
              : strictnessSettings.filter(
                  (setting) => setting !== strictnessSetting,
                ),
          );
        },
      };
    },
    [strictnessSettings],
  );

  return (
    <div className="space-y-4">
      <DialogTitle>Assignment settings</DialogTitle>
      {children}
      <TextField
        required
        name="name"
        label="Assignment name"
        placeholder="What should this assignment be called?"
        defaultValue={assignment?.name ?? pack.name}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-4">
          <TimeLimitSelect defaultValue={assignment?.timeLimit} />
          <TextField
            label="Due date"
            type="date"
            name="dueDate"
            hint="Submissions will not be accepted after this date."
            min={formatISO(today, { representation: "date" })}
            defaultValue={
              assignment?.expiresAt
                ? formatISO(assignment.expiresAt, { representation: "date" })
                : undefined
            }
          />
          <input
            type="hidden"
            name="timezoneOffset"
            value={
              // we need to send the timezone offset to the server because the
              // date is stored as UTC
              today.getTimezoneOffset()
            }
          />
          <Textarea
            name="notes"
            label="Notes"
            placeholder="Add a custom message to the pre-game screen."
            defaultValue={assignment?.notes}
          />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <RadioGroup
              label="Game mode"
              onChange={setGameMode}
              value={gameMode}
              options={[
                {
                  label: "Classic",
                  value: "classic",
                },
                {
                  label: "Multiple choice",
                  value: "mcq",
                },
              ]}
            />
            <Checkbox
              hint="Normally, players are rewarded with extra time for answering questions correctly."
              {...getCheckboxProps("DISABLE_TIME_REWARDS")}
            >
              Disable time rewards
            </Checkbox>
            <Checkbox
              hint="Seeing your overall progress through an exercise can be a good motivator for students."
              {...getCheckboxProps("ENABLE_PACK_PROGRESS_BAR")}
            >
              Show % of terms completed
            </Checkbox>
            <Fragment key={gameMode}>
              {match(gameMode)
                .with("classic", () => (
                  <>
                    <Checkbox
                      hint='Hiding the "Starts with _" placeholder makes the game more challenging.'
                      {...getCheckboxProps("HIDE_FIRST_LETTER")}
                    >
                      Hide first letter
                    </Checkbox>
                    <Checkbox
                      hint="Typing feedback shows which letters are correct and incorrect as you type."
                      {...getCheckboxProps("HIDE_PROGRESS_BAR")}
                    >
                      Hide typing feedback
                    </Checkbox>
                    <Checkbox
                      hint="Normally, the option to reveal the next letter is awarded after a streak of 5 correct answers."
                      {...getCheckboxProps("DISABLE_HINT")}
                    >
                      Disable hints
                    </Checkbox>
                  </>
                ))
                .with("mcq", () => (
                  <>
                    <input
                      type="hidden"
                      name="settings"
                      value="MULTIPLE_CHOICE"
                    />
                    {
                      // pack must contain at least one image clue
                      pack._count.words > 0 && (
                        <Checkbox {...getCheckboxProps("IMAGE_MATCH")}>
                          Image match mode
                        </Checkbox>
                      )
                    }
                  </>
                ))
                .exhaustive()}
            </Fragment>
          </div>
          {gameMode === "classic" && (
            <div className="space-y-0.5 border px-3 py-2.5">
              <Checkbox
                checked={allStrictnessSettingsEnabled}
                onChange={(event) => {
                  setStrictnessSettings(
                    event.target.checked ? allStrictnessSettings : [],
                  );
                }}
                indeterminate={
                  strictnessSettings.length > 0 && !allStrictnessSettingsEnabled
                }
              >
                All strictness rules
              </Checkbox>
              <Checkbox {...getStrictnessCheckboxProps("STRICT_ACCENTS")}>
                Strict accents
              </Checkbox>
              <Checkbox
                {...getStrictnessCheckboxProps("STRICT_CAPITALIZATION")}
              >
                Strict capitalization
              </Checkbox>
              <Checkbox {...getStrictnessCheckboxProps("STRICT_PUNCTUATION")}>
                Strict punctuation
              </Checkbox>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
